import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { useTheme } from "../../contexts/ThemeContext";
import { adminReset2fa } from "../../utils/restAPI";
import { isCurrentUserAdmin } from "../../utils/authRoles";
import { ManagerOverviewStatCard } from "../ManagerOverview/ManagerOverviewStatCard";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LockResetIcon from "@mui/icons-material/LockReset";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

const AdminTools = () => {
  const { currentTheme: theme } = useTheme();
  const isAdmin = isCurrentUserAdmin();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lastResetEmail, setLastResetEmail] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resetHistory, setResetHistory] = useState([]);

  const headCellSx = {
    fontWeight: 600,
    fontSize: "12px",
    backgroundColor: theme?.palette?.background?.tableHeader || "rgba(255,255,255,0.05)",
    color: theme?.palette?.text?.primary || "#FFFFFF",
    borderBottom: `2px solid ${theme?.palette?.divider || "#424242"}`,
    whiteSpace: "nowrap",
    py: 1.25,
  };

  const bodyCellSx = {
    fontSize: "13px",
    borderColor: theme?.palette?.divider || "#424242",
    color: theme?.palette?.text?.primary || "#FFFFFF",
    py: 1.25,
  };

  if (!isAdmin) {
    return (
      <>
        <Helmet>
          <title>Admin tools | Vantage Alpha</title>
        </Helmet>
        <Box
          sx={{
            backgroundColor:
              theme?.palette?.mode === "light" ? "#ebebeb" : theme?.palette?.grey?.[50] || "#212121",
            background:
              theme?.palette?.mode === "light" ? "#ebebeb" : theme?.palette?.grey?.[50] || "#212121",
            minHeight: "100vh",
            py: 3,
            color: theme?.palette?.text?.primary || "#FFFFFF",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1640,
              mx: "auto",
              px: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme?.palette?.text?.primary || "#FFFFFF",
                  mb: 1,
                }}
              >
                Admin tools
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme?.palette?.text?.secondary || "#B0B0B0",
                  maxWidth: 900,
                }}
              >
                Administrative control center for security governance and user management.
              </Typography>
            </Box>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: theme?.palette?.divider || "#424242",
                backgroundColor: theme?.palette?.background?.paper || "#1E1E1E",
                p: 4,
                textAlign: "center",
              }}
            >
              <Alert severity="error" sx={{ maxWidth: 600, mx: "auto" }}>
                Access Denied: You must be an administrator to access this page.
              </Alert>
            </Card>
          </Container>
        </Box>
      </>
    );
  }

  const handleOpenConfirm = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter a user email.");
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    const targetEmail = email.trim();
    setConfirmDialogOpen(false);
    setLoading(true);
    setSuccess(false);

    try {
      await adminReset2fa(targetEmail);
      setSuccess(true);
      setLastResetEmail(targetEmail);
      toast.success(`2FA has been successfully reset for ${targetEmail}`);

      // Add to session reset history
      const now = new Date();
      const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      setResetHistory((prev) => [
        {
          email: targetEmail,
          timestamp: timeStr,
          action: "2FA Disabled & Backup Codes Cleared",
          status: "Success",
        },
        ...prev,
      ]);

      setEmail("");
    } catch (error) {
      toast.error(error.message || "Failed to reset 2FA.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin tools | Vantage Alpha</title>
      </Helmet>

      <Box
        sx={{
          backgroundColor:
            theme?.palette?.mode === "light" ? "#ebebeb" : theme?.palette?.grey?.[50] || "#212121",
          background:
            theme?.palette?.mode === "light" ? "#ebebeb" : theme?.palette?.grey?.[50] || "#212121",
          minHeight: "100vh",
          py: 3,
          color: theme?.palette?.text?.primary || "#FFFFFF",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: 1640,
            mx: "auto",
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Header Section matching Managers overview */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme?.palette?.text?.primary || "#FFFFFF",
                mb: 1,
              }}
            >
              Admin tools
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme?.palette?.text?.secondary || "#B0B0B0",
                maxWidth: 900,
              }}
            >
              Administrative control center for security governance, user credential management, and authentication recovery across Vantage Alpha.
            </Typography>
          </Box>

          {/* Overview Stat Cards Row matching Managers overview */}
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Authentication"
                subtitle="Privilege level"
                value="Administrator"
                icon={AdminPanelSettingsIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Security recovery"
                subtitle="2FA reset tool"
                value="Active"
                icon={LockResetIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="MFA policy"
                subtitle="Allowed methods"
                value="TOTP / Backup"
                icon={VpnKeyOutlinedIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Target realm"
                subtitle="User directory"
                value="Cognito Auth"
                icon={VerifiedUserOutlinedIcon}
              />
            </Box>
          </Box>

          {/* Tool Card matching Manager directory card style */}
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: theme?.palette?.divider || "#424242",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              backgroundColor: theme?.palette?.background?.paper || "#1E1E1E",
              mb: 3,
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                borderBottom: `1px solid ${theme?.palette?.divider || "#424242"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme?.palette?.text?.primary || "#FFFFFF" }}>
                  Reset user 2FA
                </Typography>
                <Typography variant="caption" sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0" }}>
                  Disable two-factor authentication and clear backup codes for user accounts requiring emergency recovery.
                </Typography>
              </Box>
              <Chip
                size="small"
                icon={<SecurityOutlinedIcon style={{ fontSize: 15 }} />}
                label="Security utility"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: alpha(theme?.palette?.primary?.main || "#1976d2", 0.12),
                  color: theme?.palette?.primary?.main || "#64B5F6",
                  border: `1px solid ${alpha(theme?.palette?.primary?.main || "#1976d2", 0.25)}`,
                }}
              />
            </Box>

            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Informational Callout */}
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme?.palette?.divider || "#424242"}`,
                  backgroundColor:
                    theme?.palette?.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "rgba(0, 0, 0, 0.02)",
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    color: theme?.palette?.info?.main || "#2196F3",
                    fontSize: 20,
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0", lineHeight: 1.6 }}
                >
                  Use this tool to completely disable Two-Factor Authentication (TOTP) and clear existing backup codes for a user who has lost access to their authenticator device. Upon their next login, the user will be authenticated using their password and will be prompted to re-enroll in 2FA.
                </Typography>
              </Box>

              {/* Success Notification */}
              {success && (
                <Alert
                  severity="success"
                  onClose={() => setSuccess(false)}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme?.palette?.success?.main || "#4CAF50", 0.3)}`,
                  }}
                >
                  2FA was successfully disabled for <strong>{lastResetEmail}</strong>. The user can now log in with just their password.
                </Alert>
              )}

              {/* Reset Form */}
              <form onSubmit={handleOpenConfirm}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    maxWidth: 700,
                  }}
                >
                  <TextField
                    fullWidth
                    label="User email"
                    variant="outlined"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@vantage.com"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor:
                          theme?.palette?.mode === "dark"
                            ? "rgba(255, 255, 255, 0.03)"
                            : "rgba(0, 0, 0, 0.02)",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading || !email.trim()}
                    startIcon={!loading && <LockResetIcon />}
                    sx={{
                      height: "56px",
                      px: 3.5,
                      whiteSpace: "nowrap",
                      minWidth: 150,
                      fontWeight: 600,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Reset 2FA"}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>

          {/* Session Reset Activity Table matching Managers overview directory */}
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: theme?.palette?.divider || "#424242",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              backgroundColor: theme?.palette?.background?.paper || "#1E1E1E",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                borderBottom: `1px solid ${theme?.palette?.divider || "#424242"}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme?.palette?.text?.primary || "#FFFFFF" }}>
                Session reset activity
              </Typography>
              <Typography variant="caption" sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0" }}>
                Audit trail of 2FA resets completed during your current active session.
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: "auto", overflowY: "visible" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>User email</TableCell>
                    <TableCell sx={headCellSx}>Timestamp</TableCell>
                    <TableCell sx={headCellSx}>Action taken</TableCell>
                    <TableCell sx={headCellSx} align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resetHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={bodyCellSx} align="center">
                        <Typography sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0", py: 3, fontSize: "0.875rem" }}>
                          No 2FA resets performed during this session.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    resetHistory.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row.email}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.timestamp}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.action}</TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          <Chip
                            size="small"
                            label="Success"
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Container>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !loading && setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${theme?.palette?.divider || "#424242"}`,
            backgroundColor: theme?.palette?.background?.paper || "#1E1E1E",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: theme?.palette?.text?.primary || "#FFFFFF", pb: 1 }}>
          Confirm 2FA Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0" }}>
            Are you sure you want to disable Two-Factor Authentication and clear backup codes for{" "}
            <strong style={{ color: theme?.palette?.text?.primary || "#FFFFFF" }}>{email.trim()}</strong>?
          </DialogContentText>
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: alpha(theme?.palette?.warning?.main || "#FF9800", 0.08),
              border: `1px solid ${alpha(theme?.palette?.warning?.main || "#FF9800", 0.2)}`,
            }}
          >
            <Typography variant="caption" sx={{ color: theme?.palette?.warning?.main || "#FFB74D", display: "block" }}>
              The user will be able to log in with just their password and will be prompted to re-configure MFA on their next sign-in.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={loading}
            sx={{ color: theme?.palette?.text?.secondary || "#B0B0B0" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReset}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockResetIcon />}
            sx={{ fontWeight: 600 }}
          >
            {loading ? "Resetting..." : "Confirm Reset"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminTools;
